/* eslint-disable global-require */
import React from 'react';
import styles from './css/PersonHero.styl';
import appConfig from '../../../../../appConfig/AppConfig';
import _ from 'lodash';
import imageChecker from '../../../../shared/utilities/imageChecker';
import PortraitImage from './PortraitImage';
import HeroButtons from './HeroButtons';
import BreadCrumbs from './BreadCrumbs';
import {FormControls} from '../../../../shared/library';
import {bindMethods} from '../../../../shared/utilities/componentHelpers';
import {buildUrl} from '../../../../shared/utilities/urlHelpers';
import {
  HandleShareClick,
  ShareIcons,
} from '../../../../shared/utilities/shareHelpers';
import {
  getPageTypeBackgroundImage,
  isBatesvillePageType,
} from '../../../../shared/utilities/personPageType';
import moment from 'moment';

const {Button} = FormControls;

/**
 * PersonHero Module
 * @class  PersonHero
 */
export class PersonHero extends React.Component {
  static propTypes = {
    canonicalUrl: React.PropTypes.string,
    flowerClickEvents: React.PropTypes.func,
    flowersLink: React.PropTypes.string,
    funeralHomeAttribution: React.PropTypes.string,
    galleryCount: React.PropTypes.number,
    isSingleColumn: React.PropTypes.bool,
    scrollToGuestbook: React.PropTypes.func,
    person: React.PropTypes.object,
    personPageType: React.PropTypes.string,
    showGalleryHandler: React.PropTypes.func,
    title: React.PropTypes.string,
  };

  /**
   * Constructor
   * @param  {Object}     props props object
   * @return {Undefined}  Returns nothing
   */
  constructor(props) {
    super(props);

    const {
      dateOfBirth,
      dateOfDeath,
      fromToYears,
      age,
      location,
      locationDisplay,
      obitSources = {},
      obituaries = [],
    } = props.person || {};
    const {displayText: [obitId] = []} = obitSources;
    const obituarySource = _.find(obituaries, ['sourceId', obitId]) || {};

    this.obituarySourceLink = obituarySource.href;

    this.state = {portraitImage: true};

    this.config = appConfig();
    this.backgroundImg = require(`./assets/${getPageTypeBackgroundImage(
      props.personPageType
    )}`);
    this.windowSizeBoundary = 600;
    this.maxBackgroundPhotos = 1;
    this.lifeSpan = this.getLifeSpan(
      dateOfBirth,
      dateOfDeath,
      fromToYears,
      age
    );
    this.location = this.getLocation(location);
    this.locationDisplay = isBatesvillePageType(props.personPageType)
      ? null
      : this.getLocationDisplay(locationDisplay);
    this.shareItems = ShareIcons(props.canonicalUrl, props.title);

    bindMethods(this, [
      'handleSendFlowersClick',
      'handleShareClick',
      'handleWindowLoad',
    ]);
  }

  /**
   * Lifecycle
   * @returns {Undefined} Returns nothing
   */
  componentDidMount() {
    window.addEventListener('load', this.handleWindowLoad);
  }

  /**
   * Lifecycle
   * @returns {Undefined} Returns nothing
   */
  componentWillUnmount() {
    window.removeEventListener('load', this.handleWindowLoad);
  }

  /**
   * Checks if a portrait image exists and sets the state
   * @param  {String} imageUrl The image's URL
   * @return {Undefined} Returns nothing
   */
  checkPortraitImage(imageUrl) {
    const image = imageChecker(imageUrl);

    if (!image) {
      this.setState({portraitImage: false});
    }
  }

  /**
   * Handles when window is loaded and fires a check on the portrait image
   * @return {Undefined} Returns nothing
   */
  handleWindowLoad() {
    if (_.get(this.props.person, 'portraitImage.href')) {
      this.checkPortraitImage(
        `${buildUrl(
          this.config.environmentalUrl.cacheUrl,
          this.props.person.portraitImage.href
        )}`
      );
    }
  }

  /**
   * Creates a string of the person's birth date, death date, and age
   * @param  {String} dateOfBirth Person's birthdate
   * @param  {String} dateOfDeath Person's death date
   * @param  {String} fromToYears Person's birth year and death year (1981 - 2081)
   * @param  {Number} age Person's age
   * @returns {String} Returns a string representing the date that the person lived
   */
  getLifeSpan(dateOfBirth, dateOfDeath, fromToYears, age) {
    let dateString = '';

    // Birth Range
    if (dateOfBirth && dateOfDeath) {
      const dob = moment.utc(dateOfBirth, 'MMMM D, YYYY');
      const formattedDob = dob.format('MMM D, YYYY');
      const dod = moment.utc(dateOfDeath, 'MMMM D, YYYY');
      const formattedDod = dod.format('MMM D, YYYY');

      dateString += `${formattedDob} - ${formattedDod}`;
    } else if (fromToYears) {
      dateString += fromToYears;
    }
    // Age
    if (age) {
      // There is no birth range so no paren's
      if (dateString === '') {
        dateString += `Age ${age}`;
      } else {
        dateString += ` (Age ${age})`;
      }
    }
    return dateString;
  }

  /**
   * Get either the city or the state or both
   * @method getLocation
   * @param  {Object}  location Person's location
   * @return {String}  Returns a person's location
   */
  getLocation(location) {
    const {city, state} = location || {};
    const text = [city, state].filter(place => place).join(', ');

    return text;
  }

  /**
   * Get either the city or the state or both
   * @method getLocationDisplay
   * @param  {Object}  locationDisplay Person's locationDisplay
   * @return {String}  Returns a person's location
   */
  getLocationDisplay(locationDisplay) {
    const {city, state, country} = locationDisplay || {};

    return _.compact([city, state, country]).join(', ');
  }

  /**
   * Constructs the URL for the See More link
   * @param  {String} firstName First name to search with
   * @param  {String} lastName  Last name to search with
   * @param  {String} domain The domain to search at
   * @return {String} The constructed See More link
   */
  getSearchUrl(firstName, lastName, domain) {
    const queryParams =
      firstName && lastName
        ? `?daterange=all&firstname=${firstName}&lastname=${lastName}`
        : '';

    return `${domain}/ns/obitfinder/obituary-search.aspx${queryParams}`;
  }

  /**
   * Renders Component
   * @return {JSX} Rendered PersonHero Component
   */
  render() {
    const {
      flowersLink,
      flowerClickEvents,
      funeralHomeAttribution,
      galleryCount,
      isSingleColumn,
      person,
      personPageType,
      scrollToGuestbook,
      showGalleryHandler,
    } = this.props;

    const {name, portraitImage, guestBook, byPageLink, sourceId, personLayout} =
      person || {};

    const {first, last, full} = name || {};

    const {
      allowGuestBook,
      allowGuestBookEntry,
      guestBookLink,
      guestBookGALabel,
      totalCondolencesCount,
    } = guestBook || {};

    const {pageTypeClass} = personLayout || {};

    return (
      <div className={styles[pageTypeClass] || styles.PersonHero}>
        <div className={styles.background}>
          <img src={this.backgroundImg} />
          <div className={styles.gradient} />
        </div>
        <div className={styles.content}>
          <BreadCrumbs
            byPageUrl={`${this.config.vanityDomain}/obituaries/local`}
            name={name}
            pageTypeClass={pageTypeClass}
          />
          <div className={styles.infoGroup}>
            <div className={styles.personHeroLeft}>
              <PortraitImage
                attribution={funeralHomeAttribution}
                cacheUrl={this.config.environmentalUrl.cacheUrl}
                firstname={first}
                galleryCount={galleryCount}
                isValidImage={this.state.portraitImage}
                lastname={last}
                portraitImage={_.get(portraitImage, 'href')}
                showGalleryHandler={showGalleryHandler}
                showPortrait={this.state.portraitImage}
              />
            </div>
            <div className={styles.personHeroRight}>
              <h1 className={styles.name}>{full ? full : ''}</h1>
              <p className={styles.date}>{this.lifeSpan}</p>
              <p className={styles.location}>{this.locationDisplay}</p>
              <HeroButtons
                allowGuestBook={allowGuestBook}
                allowGuestBookEntry={allowGuestBookEntry}
                allowPersonPagePosting
                fhShowFlowers={_.get(personLayout, 'fhShowFlowers')}
                flowerClickEvents={flowerClickEvents}
                flowersLink={flowersLink}
                guestBookGALabel={guestBookGALabel}
                guestBookLink={_.get(guestBookLink, 'href')}
                isSingleColumn={isSingleColumn}
                layout={_.get(personLayout, 'layout.left[0]', 'Obit')}
                obituarySourceLink={this.obituarySourceLink}
                personPageType={personPageType}
                pageTypeClass={pageTypeClass}
                scrollToGuestbook={scrollToGuestbook}
                showFlowersAndGifts={_.get(personLayout, 'showFlowersAndGifts')}
                sourceId={sourceId}
                totalCondolencesCount={totalCondolencesCount}
              />
            </div>
          </div>
          <aside className={styles.extras}>
            <div className={styles.shares}>
              {this.shareItems.map((obj, i) => (
                <Button
                  btnType="none"
                  className={styles.icons}
                  handleClick={_.partial(
                    HandleShareClick,
                    _,
                    obj.shareUrl,
                    obj.label
                  )}
                  id={obj.label}
                  key={i}
                >
                  <span className={styles[obj.label]}>{obj.svg}</span>
                </Button>
              ))}
            </div>
            <a
              className={styles.search}
              href={
                byPageLink ||
                this.getSearchUrl(first, last, this.config.vanityDomain)
              }
              target="_blank"
            >
              Not the right person? See All &rsaquo;
            </a>
          </aside>
        </div>
      </div>
    );
  }
}

export default PersonHero;



// WEBPACK FOOTER //
// ./src/reactModules/pages/Person/components/PersonHero/PersonHero.js